<template>
  <section class="section-base form-base">
    <template v-if="loaded">
      <AddStatementHeader
        :title="title"
        :steps-count="stepsCount"
        :current-step="2"
      />

      <div class="form-section-base__title">
        Собираетесь внести изменения в основное заявление?
      </div>
      <v-container>
        <div class="form-section-base__controls">
          <v-row>
            <v-col>
              <v-radio-group
                v-model="edit"
                column
                :error-messages="getValidationErrorText($v.edit)"
              >
                <v-radio label="Да" :value="true" />
                <v-radio label="Нет" :value="false" />
              </v-radio-group>
            </v-col>
          </v-row>
        </div>

        <Footer
          @prev-btn-click="prevBtnClickHandler"
          @next-btn-click="nextBtnClickHandler"
          :loading-next="isDeleteStatementLinkLoading"
        />
      </v-container>

      <v-dialog v-model="dialogArchiveOpen" max-width="600px">
        <div class="form">
          <div class="form-base__title mb-4">{{ dialogArchiveTitle }}</div>

          <p>
            Если вы собираетесь заново начать работу над заявлением с
            дополнительными документами, то ранее внесенные изменения будут
            удалены.
          </p>

          <v-row justify="end">
            <v-col cols="auto">
              <v-btn
                @click="openDialogDelete"
                depressed
                color="primary"
                class="button-base"
              >
                Начать заново
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                @click="
                  goToSign();
                  closeDialogArchive();
                "
                :loading="isArchiveFormedLoading"
                depressed
                color="primary"
                class="button-base"
              >
                Продолжить работу
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-dialog>

      <v-dialog persistent v-model="dialogDeleteOpen" max-width="600px">
        <div class="form">
          <div class="form-base__title mb-4">Подтверждение</div>

          <div class="form-base__title mb-4">
            Вы действительно хотите удалить ранее внесенные изменения?
          </div>

          <p>Отменить это действие будет невозможно.</p>

          <v-row justify="end">
            <v-col cols="auto">
              <v-btn
                depressed
                color="primary"
                class="button-base"
                @click="closeDialogDelete"
              >
                Нет
              </v-btn>
            </v-col>

            <v-col cols="auto">
              <v-btn
                depressed
                color="primary"
                class="button-base"
                :loading="isDeleteStatementLinkLoading"
                @click="deleteConfirmedHandler"
              >
                Да
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-dialog>
    </template>
    <SkeletonLoader v-if="loading" />
  </section>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

import Footer from '@/components/services/elements/Footer';
import { getValidationErrorText } from '@/utils/validate';
import { EditedStatementModule } from '@/store/modules/alternativeServices/editedStatement';
import { RouteName } from '@/router';
import { AddStModule } from '@/store/modules/alternativeServices/additionalStatement';
import SkeletonLoader from '@/components/base/SkeletonLoader';
import AddStatementHeader from '@/views/alternativeServices/AddStatementHeader';
import { mapStatementName } from '@/utils/services';
import {
  addDocumentsStepsCount,
  addDocumentsWithEdit,
  getAddDocumentsBreadcrumbs,
} from '@/utils/common';
import { BreadCrumbsModule } from '@/store/modules/breadCrumbs';

export default {
  name: 'AddDocumentsEdit',
  props: {
    altStatementId: {
      type: String,
      required: true,
    },
  },
  components: {
    Footer,
    SkeletonLoader,
    AddStatementHeader,
  },
  data: () => ({
    getValidationErrorText,
    edit: null,
    dialogArchiveOpen: false,
    dialogDeleteOpen: false,
    archiveFormedInterval: null,
  }),
  computed: {
    title() {
      return mapStatementName[this.addStatement.originalStatementType];
    },

    stepsCount() {
      return addDocumentsStepsCount(this.withEdit);
    },

    withEdit() {
      return addDocumentsWithEdit(this.addStatement);
    },

    isDeleteStatementLinkLoading() {
      return (
        AddStModule.deleteStLinkState.loading ||
        AddStModule.addStatement.fetching
      );
    },

    dialogArchiveTitle() {
      const unformedTitle = 'Идет загрузка архива ранее измененного заявления';
      const formedTitle = 'Архив сформирован';

      return this.isArchiveFormed ? formedTitle : unformedTitle;
    },

    isArchiveFormed() {
      return EditedStatementModule.isArchiveFormed.entity;
    },

    isArchiveFormedLoading() {
      return EditedStatementModule.isArchiveFormed.loading;
    },

    isArchiveStartedFormed() {
      return this.addStatement.changedStatementZipLink;
    },

    isEditedStatementExist() {
      return this.addStatement.changedStatementId;
    },

    addStatement() {
      return AddStModule.addStatement.entity;
    },

    loading() {
      return AddStModule.addStatement.loading;
    },
    loaded() {
      return AddStModule.addStatement.loaded;
    },
  },
  watch: {
    isArchiveFormed(formed) {
      if (formed) this.endCheckIsArchiveFormed();
    },
  },
  validations: {
    edit: {
      required,
    },
  },
  async created() {
    await AddStModule.getAddStById(this.altStatementId);

    if (this.isArchiveStartedFormed) {
      this.startCheckIsArchiveFormed();
    }

    BreadCrumbsModule.SET(
      getAddDocumentsBreadcrumbs(this.addStatement.originalStatementId)
    );
  },
  beforeDestroy() {
    this.endCheckIsArchiveFormed();
    BreadCrumbsModule.SET(null);
  },
  methods: {
    async nextBtnClickHandler() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        await this.$vuetify.goTo('#scroll-target', { duration: 200 });
        return;
      }

      if (this.edit) {
        this.nextEdit();
      } else await this.nextSign();
    },

    prevBtnClickHandler() {
      this.$router.push({
        name: RouteName.ADD_DOCUMENTS_APPLICANT,
        params: {
          altStatementId: this.altStatementId,
        },
      });
    },

    goToEdit() {
      this.$router.push({
        name: `${RouteName.ALTERNATIVE_SERVICE_EDIT}-${
          RouteName[this.addStatement.originalStatementType]
        }`,
        params: { originalStatementId: this.addStatement.originalStatementId },
      });
    },

    goToSign() {
      this.$router.push({
        name: RouteName.ADD_DOCUMENTS_SIGN,
        params: {
          altStatementId: this.altStatementId,
        },
      });
    },

    openDialogArchive() {
      this.dialogArchiveOpen = true;
    },

    closeDialogArchive() {
      this.dialogArchiveOpen = false;
    },

    openDialogDelete() {
      this.dialogDeleteOpen = true;
    },

    closeDialogDelete() {
      this.dialogDeleteOpen = false;
    },

    startCheckIsArchiveFormed() {
      EditedStatementModule.checkIsArchiveFormed(
        this.addStatement.originalStatementId
      );

      this.archiveFormedInterval = setInterval(() => {
        EditedStatementModule.checkIsArchiveFormed(
          this.addStatement.originalStatementId
        );
      }, 3000);
    },

    endCheckIsArchiveFormed() {
      clearInterval(this.archiveFormedInterval);
    },

    nextEdit() {
      if (this.isArchiveStartedFormed) {
        this.openDialogArchive();
      } else this.goToEdit();
    },

    async nextSign() {
      if (this.isEditedStatementExist) {
        this.openDialogDelete();
      } else await this.deleteConfirmedHandler();
    },

    async deleteConfirmedHandler() {
      await AddStModule.deleteStLink(this.addStatement.id);
      await AddStModule.updateAddStById(this.addStatement.id);

      if (this.edit) {
        this.goToEdit();
      } else {
        this.goToSign();
        this.closeDialogDelete();
      }
    },
  },
};
</script>

<style scoped>
.form {
  padding: 24px 24px 12px;
  background-color: #fff;
}
</style>
